import { useEffect, useState } from 'react'
import { ShieldCheck, Info, Search, Eye, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import api from '../services/api'

// Log de Auditoria (mesmo layout do log do FrotaTrack): frota.logsis do ERP + web, ações agrupadas por salvamento.
interface Campo { campo: string; antes: string; depois: string; mudou: boolean }
interface Evento {
  hora: string; nome: string; prg: string; tela: string; registro: string
  ope: string; acao: string; campos: Campo[]; resumo: string; origem: 'Web' | 'ERP'; modulo: string
}
interface Resumo { alteracoes7d: number; quemMaisAlterou: { nome: string; qtd: number } | null; telaMaisAlterada: { tela: string; qtd: number } | null }
interface Opcoes { usuarios: string[]; telas: { prg: string; nome: string; modulo: string }[]; modulos: { k: string; nome: string }[] }

const AZUL = '#1b3f7b'
const ACOES_FILTRO = [{ v: 'INS', t: 'Inserir' }, { v: 'UPD', t: 'Editar' }, { v: 'DLT', t: 'Excluir' }, { v: 'ACE', t: 'Acesso (login)' }]
const COR_ACAO: Record<string, { bg: string; fg: string }> = {
  INS: { bg: '#dcfce7', fg: '#15803d' }, UPD: { bg: '#e0f2fe', fg: '#0369a1' },
  DLT: { bg: '#fee2e2', fg: '#b91c1c' }, ACE: { bg: '#ede9fe', fg: '#6d28d9' },
}

const pad = (n: number) => String(n).padStart(2, '0')
const localISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
const inicioDia = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const fimDoDia = () => `${localISO(new Date()).slice(0, 10)}T23:59`
const dataBr = (h: string) => `${h.slice(8, 10)}/${h.slice(5, 7)}/${h.slice(0, 4)} ${h.slice(11)}`

function haQuanto(h: string): string {
  const s = (Date.now() - new Date(h.replace(' ', 'T')).getTime()) / 1000
  if (s < 60) return 'agora'
  if (s < 3600) return `há ${Math.floor(s / 60)} min`
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`
  const d = Math.floor(s / 86400)
  return d === 1 ? 'há 1 dia' : `há ${d} dias`
}

const sel: React.CSSProperties = { width: '100%', height: 32, border: '1px solid #d1d5db', borderRadius: 6, padding: '0 8px', fontSize: '0.84rem', background: '#fff' }
const lbl: React.CSSProperties = { fontSize: '0.74rem', color: '#374151', fontWeight: 600, marginBottom: 3, display: 'block' }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.55rem 0.8rem', flex: '0 1 230px', minWidth: 170, position: 'relative' }

function Cartao({ titulo, valor, dica }: { titulo: string; valor: string; dica: string }) {
  return (
    <div style={card}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{titulo}</div>
      <span title={dica} style={{ position: 'absolute', top: 12, right: 12, color: '#9ca3af', cursor: 'help' }}><Info size={15} /></span>
      <div style={{ fontSize: '0.98rem', fontWeight: 700, color: '#111827', marginTop: 3 }}>{valor}</div>
    </div>
  )
}

export default function LogSistema() {
  const aba = 'tudo' // sem abas: traz alterações e acessos juntos (o filtro Ação separa)
  const [usuario, setUsuario] = useState('')
  const [acao, setAcao] = useState('')
  const [tela, setTela] = useState('')
  const [texto, setTexto] = useState('')
  const [de, setDe] = useState(localISO(inicioDia(new Date())))  // padrão: hoje
  const [ate, setAte] = useState(fimDoDia())
  const [pagina, setPagina] = useState(1)

  const [dados, setDados] = useState<{ total: number; eventos: Evento[] } | null>(null)
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [opcoes, setOpcoes] = useState<Opcoes>({ usuarios: [], telas: [], modulos: [] })
  const [modulo, setModulo] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')
  const [detalhe, setDetalhe] = useState<Evento | null>(null)

  const params = () => ({ aba, modulo: modulo || undefined, usuario: usuario || undefined, acao: acao || undefined, tela: tela || undefined, texto: texto || undefined, de: de || undefined, ate: ate || undefined })

  async function buscar(p = 1) {
    setCarregando(true); setErro('')
    try {
      const r = await api.get('/erp/log-auditoria', { params: { ...params(), pagina: p } })
      setDados(r.data); setPagina(p)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao consultar o log.')
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    api.get('/erp/log-auditoria/resumo').then(r => setResumo(r.data)).catch(() => {})
    api.get('/erp/log-auditoria/opcoes').then(r => setOpcoes(r.data)).catch(() => {})
  }, [])
  useEffect(() => { buscar(1) }, [aba, modulo, usuario, acao, tela, de, ate]) // eslint-disable-line react-hooks/exhaustive-deps

  function atalho(tipo: 'hoje' | '7' | '30' | 'mes') {
    const agora = new Date()
    const ini = tipo === 'hoje' ? inicioDia(agora)
      : tipo === '7' ? inicioDia(new Date(Date.now() - 6 * 86400000))
      : tipo === '30' ? inicioDia(new Date(Date.now() - 29 * 86400000))
      : new Date(agora.getFullYear(), agora.getMonth(), 1)
    setDe(localISO(ini)); setAte(fimDoDia())
  }

  async function exportar(formato: 'excel' | 'pdf') {
    try {
      const r = await api.get(`/erp/log-auditoria/${formato}`, { params: params(), responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      if (formato === 'pdf') window.open(url, '_blank')
      else { const a = document.createElement('a'); a.href = url; a.download = 'log-auditoria.xlsx'; a.click() }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { setErro('Erro ao exportar o log.') }
  }

  const total = dados?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / 20))
  const ini = total ? (pagina - 1) * 20 + 1 : 0
  const fim = Math.min(pagina * 20, total)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={20} color="#ea580c" />
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>Log de Auditoria</h2>
          <span style={{ color: '#6b7280', fontSize: '0.95rem' }}>({total.toLocaleString('pt-BR')})</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => exportar('excel')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', fontWeight: 600, cursor: 'pointer' }}>
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button onClick={() => exportar('pdf')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>
            <FileText size={16} /> PDF
          </button>
        </div>
      </div>

      {/* Cartões */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Cartao titulo="Registros" valor={total.toLocaleString('pt-BR')} dica="Ações encontradas com os filtros atuais" />
        <Cartao titulo="Alterações (7 dias)" valor={(resumo?.alteracoes7d ?? 0).toLocaleString('pt-BR')} dica="Inclusões, alterações e exclusões nos últimos 7 dias (ERP e web)" />
        <Cartao titulo="Quem mais alterou (7 dias)" valor={resumo?.quemMaisAlterou ? `${resumo.quemMaisAlterou.nome} (${resumo.quemMaisAlterou.qtd})` : '—'} dica="Usuário com mais ações nos últimos 7 dias" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div style={{ width: 230 }}><label style={lbl}>Módulo</label>
          <select style={sel} value={modulo} onChange={e => { setModulo(e.target.value); setTela('') }}>
            <option value="">Todos</option>{opcoes.modulos.map(m => <option key={m.k} value={m.k}>{m.nome}</option>)}
          </select></div>
        <div style={{ width: 150 }}><label style={lbl}>Usuário</label>
          <select style={sel} value={usuario} onChange={e => setUsuario(e.target.value)}>
            <option value="">Todos</option>{opcoes.usuarios.map(u => <option key={u} value={u}>{u}</option>)}
          </select></div>
        <div style={{ width: 110 }}><label style={lbl}>Ação</label>
          <select style={sel} value={acao} onChange={e => setAcao(e.target.value)}>
            <option value="">Todas</option>{ACOES_FILTRO.map(a => <option key={a.v} value={a.v}>{a.t}</option>)}
          </select></div>
        <div style={{ width: 260 }}><label style={lbl}>Tela</label>
          <select style={sel} value={tela} onChange={e => setTela(e.target.value)}>
            <option value="">Todas</option>{opcoes.telas.filter(t => !modulo || t.modulo === opcoes.modulos.find(m => m.k === modulo)?.nome)
              .map(t => <option key={t.prg} value={t.prg}>{t.nome === t.prg ? t.prg : `${t.nome} (${t.prg})`}</option>)}
          </select></div>
        <div style={{ width: 280 }}><label style={lbl}>Buscar no registro</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={sel} value={texto} onChange={e => setTexto(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar(1)} placeholder="Nome, placa, natureza, valor..." />
            <button onClick={() => buscar(1)} style={{ width: 34, height: 32, borderRadius: 6, border: 'none', background: AZUL, color: '#fff', cursor: 'pointer', flexShrink: 0 }}><Search size={16} /></button>
          </div></div>
      </div>
      <div>
        <label style={lbl}>Período <span style={{ fontWeight: 400, color: '#6b7280' }}>(horário de Brasília)</span></label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>De</span>
          <input type="datetime-local" style={{ ...sel, width: 185 }} value={de} onChange={e => setDe(e.target.value)} />
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>até</span>
          <input type="datetime-local" style={{ ...sel, width: 185 }} value={ate} onChange={e => setAte(e.target.value)} />
          {([['hoje', 'Hoje'], ['7', '7 dias'], ['30', '30 dias'], ['mes', 'Este mês']] as const).map(([k, t]) => (
            <button key={k} onClick={() => atalho(k)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: '0.8rem', cursor: 'pointer' }}>{t}</button>
          ))}
        </div>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {/* Tabela */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
            <thead>
              <tr style={{ background: AZUL, color: '#fff', textAlign: 'left' }}>
                {['Data/Hora', 'Usuário', 'Tela', 'Ação', 'O que foi feito', 'Origem', 'Ações'].map((h, i) => (
                  <th key={h} style={{ padding: '12px 14px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', textAlign: i === 6 ? 'center' : 'left' }}>
                    {h} {i === 0 && <ChevronDown size={12} style={{ verticalAlign: 'middle' }} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carregando && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Carregando...</td></tr>}
              {!carregando && dados?.eventos.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                  Nenhum registro no período.
                </td></tr>
              )}
              {!carregando && dados?.eventos.map((e, i) => {
                const cor = COR_ACAO[e.ope] || { bg: '#f3f4f6', fg: '#374151' }
                const outros = e.ope === 'UPD' && e.campos.length > 1 ? e.campos.filter(c => c.mudou).map(c => c.campo).filter(Boolean) : []
                return (
                  <tr key={`${e.hora}-${e.nome}-${e.prg}-${e.registro}-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151' }}>{dataBr(e.hora)}</div>
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{haQuanto(e.hora)}</div>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#111827' }}>{e.nome}</td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>
                      {e.modulo && <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>{e.modulo}</div>}
                      {e.tela}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: cor.bg, color: cor.fg, borderRadius: 6, padding: '3px 8px', fontSize: '0.75rem', fontWeight: 700 }}>{e.acao}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#111827', maxWidth: 520 }}>
                      <div>{e.resumo}</div>
                      {e.registro && <div style={{ fontSize: '0.76rem', color: '#6b7280' }}>{e.registro}</div>}
                      {outros.length > 0 && <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>alterou: {outros.slice(0, 4).join(', ')}{outros.length > 4 ? ` e mais ${outros.length - 4}` : ''}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{e.origem}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      <button onClick={() => setDetalhe(e)} title="Ver detalhes" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151' }}><Eye size={17} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #e5e7eb', fontSize: '0.84rem', color: '#374151' }}>
          <span>Mostrando {ini}–{fim} de {total.toLocaleString('pt-BR')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button disabled={pagina <= 1} onClick={() => buscar(pagina - 1)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: pagina <= 1 ? 'default' : 'pointer', color: pagina <= 1 ? '#9ca3af' : '#374151' }}><ChevronLeft size={14} /> Anterior</button>
            <span>Página {pagina} de {paginas}</span>
            <button disabled={pagina >= paginas} onClick={() => buscar(pagina + 1)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: pagina >= paginas ? 'default' : 'pointer', color: pagina >= paginas ? '#9ca3af' : '#374151' }}>Próxima <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      {/* Detalhe */}
      {detalhe && (
        <div className="modal-overlay" onClick={() => setDetalhe(null)}>
          <div className="modal-box" style={{ maxWidth: 720 }} onClick={ev => ev.stopPropagation()}>
            <div className="modal-header">
              <h2>{detalhe.acao} — {detalhe.tela}</h2>
              <button className="modal-fechar" onClick={() => setDetalhe(null)}>✕</button>
            </div>
            <div className="modal-corpo" style={{ fontSize: '0.86rem' }}>
              {/* Resumo em destaque */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }}>
                <span style={{ background: (COR_ACAO[detalhe.ope] || { bg: '#f3f4f6' }).bg, color: (COR_ACAO[detalhe.ope] || { fg: '#374151' }).fg, borderRadius: 6, padding: '3px 8px', fontSize: '0.75rem', fontWeight: 700 }}>{detalhe.acao}</span>
                <span style={{ fontWeight: 700, color: '#111827', flex: 1 }}>{detalhe.resumo}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: detalhe.origem === 'Web' ? '#dbeafe' : '#f3f4f6', color: detalhe.origem === 'Web' ? '#1d4ed8' : '#374151' }}>{detalhe.origem}</span>
              </div>
              {/* Informações */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                {[
                  { r: 'Data/Hora', v: dataBr(detalhe.hora), s: haQuanto(detalhe.hora) },
                  { r: 'Usuário', v: detalhe.nome },
                  { r: 'Tela', v: detalhe.tela, s: detalhe.prg !== detalhe.tela ? detalhe.prg : '' },
                  { r: 'Módulo', v: detalhe.modulo || '—' },
                  { r: 'Registro', v: detalhe.registro || '—', largo: true },
                ].map(x => (
                  <div key={x.r} style={{ gridColumn: x.largo ? '1 / -1' : undefined, border: '1px solid #eef0f4', borderRadius: 6, padding: '6px 10px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{x.r}</div>
                    <div style={{ fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>
                      {x.v}{x.s && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.78rem' }}> · {x.s}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: AZUL, textAlign: 'left' }}>
                  {['Campo', 'Antes', 'Depois'].map(h => (
                    <th key={h} style={{ padding: '7px 8px', color: '#fff', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {/* Em edição, só os campos que mudaram — o ERP grava a tela inteira a cada salvamento */}
                  {detalhe.campos.filter(c => detalhe.ope !== 'UPD' || c.mudou).map((c, i) => {
                    const semMudanca = detalhe.ope === 'UPD' && !c.mudou
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', color: semMudanca ? '#9ca3af' : undefined }}>
                        <td style={{ padding: '6px 8px' }}>{c.campo || '—'}{semMudanca && <span style={{ fontSize: '0.7rem' }}> (sem mudança)</span>}</td>
                        <td style={{ padding: '6px 8px', color: semMudanca ? undefined : '#b91c1c' }}>{c.antes || '—'}</td>
                        <td style={{ padding: '6px 8px', color: semMudanca ? undefined : '#15803d', fontWeight: semMudanca ? 400 : 600 }}>{c.depois || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {detalhe.ope === 'UPD' && detalhe.campos.some(c => !c.mudou) && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#9ca3af' }}>
                  {detalhe.campos.filter(c => !c.mudou).length} campo(s) gravado(s) pelo sistema sem mudança de valor não aparecem aqui.
                </div>
              )}
            </div>
            <div className="modal-footer"><button className="btn btn-orange" onClick={() => setDetalhe(null)}>Fechar</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
